import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { LIST_CHAT_GQL } from '@modules/oracle/hooks/useFetchChatList';
import { OracleSearchActionType } from '@modules/oracle/store/oracleActionType';
import { useOracleSearchStore } from '@modules/oracle/store/oracleSearchStore';
import { Mutation, Status, UpdateChatInput } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const UPDATE_WISDOM_CHAT = gql`
  mutation updateChat(
    $tenantID: ID!
    $userID: ID!
    $editToken: ID!
    $title: String
    $status: Status
  ) {
    updateChat(
      request: {
        tenantID: $tenantID
        userID: $userID
        editToken: $editToken
        title: $title
        status: $status
      }
    ) {
      chat {
        createdAt
        createdBy {
          firstName
          id
          lastName
        }
        editToken
        id
        sessionID
        status
        tenantID
        title
        updatedAt
        viewToken
      }
    }
  }
`;

interface UpdateWisdomChatOptions {
  onCompleted?: (data: Mutation) => void;
}

export const useUpdateWisdomChat = ({
  onCompleted,
}: UpdateWisdomChatOptions = {}) => {
  const dispatch = useOracleSearchStore(({ dispatch }) => dispatch);
  const { showErrorNotification } = useNotificationContext();

  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const [updateWisdomChatMutation, { loading }] = useMutation<
    Mutation,
    UpdateChatInput
  >(UPDATE_WISDOM_CHAT, {
    notifyOnNetworkStatusChange: true,
    refetchQueries: [LIST_CHAT_GQL],
    onCompleted: (data) => {
      dispatch({
        type: OracleSearchActionType.SET_DATA,
        payload: {
          chat: data.updateChat?.chat,
        },
      });

      onCompleted?.(data);
    },
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to update Wisdom chat.',
        props: {},
      });
    },
  });

  const updateWisdomChat = useCallback(
    (input: { editToken: string; title?: string; status?: Status }) => {
      return updateWisdomChatMutation({
        variables: {
          tenantID,
          userID,
          ...input,
        },
      });
    },
    [updateWisdomChatMutation, tenantID, userID],
  );

  return [updateWisdomChat, { loading }] as const;
};
